import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import AnimatedCheck from '../components/AnimatedCheck';
import AnimatedError from '../components/AnimatedError';
import { Loader2, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CheckoutSuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const sessionId = params.get('session_id') || '';

  const [status, setStatus] = useState('loading'); // loading, success, error
  const [email, setEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const calledRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setErrorMsg('session_id ausente');
      return;
    }
    if (calledRef.current) return;
    calledRef.current = true;
    finalize();
  }, [sessionId]);

  const finalize = async () => {
    try {
      const res = await fetch(`${API_URL}/api/signup/checkout-finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, host_url: window.location.origin })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        // If backend returned the set_password_token, redirect directly (preferred flow)
        if (data.set_password_token) {
          navigate(`/set-password?token=${encodeURIComponent(data.set_password_token)}`, { replace: true });
          return;
        }
        setEmail(data.email || '');
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMsg(data.detail || 'Erro ao finalizar pagamento');
      }
    } catch (error) {
      setStatus('error');
      setErrorMsg('Erro ao conectar com o servidor');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Finalizando seu cadastro...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AnimatedError />
            <h2 className="text-2xl font-bold mb-2">Erro</h2>
            <p className="text-muted-foreground mb-6">{errorMsg}</p>
            <Button onClick={() => navigate('/')} data-testid="checkout-success-back">
              Voltar à Página Inicial
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 text-center">
          <AnimatedCheck />
          <h2 className="text-2xl font-bold mb-2">Pagamento Confirmado!</h2>
          <p className="text-muted-foreground mb-4">
            Sua conta foi criada com sucesso. Enviamos um email para{' '}
            <strong>{email}</strong> com instruções para você definir sua senha.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground bg-teal-50 dark:bg-teal-950/30 rounded-lg p-3 mb-6">
            <Mail className="w-4 h-4 text-teal-600" />
            <span>Verifique sua caixa de entrada (e a pasta de spam)</span>
          </div>
          <Button variant="outline" onClick={() => navigate('/login')} data-testid="checkout-success-login">
            Ir para Login
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
